/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {isPresent} from '../facade/lang';
import * as o from '../output/output_ast';
import {TemplateAst} from '../template_parser/template_ast';

import {CompileView} from './compile_view';

class _DebugState {
/**
 * @param {?} nodeIndex
 * @param {?} sourceAst
 */
constructor(public nodeIndex: number,
public sourceAst: TemplateAst) {}
}

function _DebugState_tsickle_Closure_declarations() {
 /** @type {?} */
_DebugState.prototype.nodeIndex;
 /** @type {?} */
_DebugState.prototype.sourceAst;
}


const /** @type {?} */ NULL_DEBUG_STATE = new _DebugState(null, null);

export class CompileMethod {
private _newState: _DebugState = NULL_DEBUG_STATE;
private _currState: _DebugState = NULL_DEBUG_STATE;
private _debugEnabled: boolean;
private _bodyStatements: o.Statement[] = [];
/**
 * @param {?} _view
 */
constructor(private _view: CompileView) {
    this._debugEnabled = this._view.genConfig.genDebugInfo;
  }
/**
 * @return {?}
 */
private _updateDebugContextIfNeeded() {
    if (this._newState.nodeIndex !== this._currState.nodeIndex ||
        this._newState.sourceAst !== this._currState.sourceAst) {
      const /** @type {?} */ expr = this._updateDebugContext(this._newState);
      if (isPresent(expr)) {
        this._bodyStatements.push(expr.toStmt());
      }
    }
  }
/**
 * @param {?} newState
 * @return {?}
 */
private _updateDebugContext(newState: _DebugState): o.Expression {
    this._currState = this._newState = newState;
    if (this._debugEnabled) {
      const /** @type {?} */ sourceLocation =
          isPresent(newState.sourceAst) ? newState.sourceAst.sourceSpan.start : null;

      return o.THIS_EXPR.callMethod('debug', [
        o.literal(newState.nodeIndex),
        isPresent(sourceLocation) ? o.literal(sourceLocation.line) : o.NULL_EXPR,
        isPresent(sourceLocation) ? o.literal(sourceLocation.col) : o.NULL_EXPR
      ]);
    } else {
      return null;
    }
  }
/**
 * @param {?} nodeIndex
 * @param {?} templateAst
 * @return {?}
 */
resetDebugInfoExpr(nodeIndex: number, templateAst: TemplateAst): o.Expression {
    const /** @type {?} */ res = this._updateDebugContext(new _DebugState(nodeIndex, templateAst));
    return res || o.NULL_EXPR;
  }
/**
 * @param {?} nodeIndex
 * @param {?} templateAst
 * @return {?}
 */
resetDebugInfo(nodeIndex: number, templateAst: TemplateAst) {
    this._newState = new _DebugState(nodeIndex, templateAst);
  }
/**
 * @param {...?} stmts
 * @return {?}
 */
push(...stmts: o.Statement[]) { this.addStmts(stmts); }
/**
 * @param {?} stmt
 * @return {?}
 */
addStmt(stmt: o.Statement) {
    this._updateDebugContextIfNeeded();
    this._bodyStatements.push(stmt);
  }
/**
 * @param {?} stmts
 * @return {?}
 */
addStmts(stmts: o.Statement[]) {
    this._updateDebugContextIfNeeded();
    this._bodyStatements.push(...stmts);
  }
/**
 * @return {?}
 */
finish(): o.Statement[] { return this._bodyStatements; }
/**
 * @return {?}
 */
isEmpty(): boolean { return this._bodyStatements.length === 0; }
}

function CompileMethod_tsickle_Closure_declarations() {
 /** @type {?} */
CompileMethod.prototype._newState;
 /** @type {?} */
CompileMethod.prototype._currState;
 /** @type {?} */
CompileMethod.prototype._debugEnabled;
 /** @type {?} */
CompileMethod.prototype._bodyStatements;
 /** @type {?} */
CompileMethod.prototype._view;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
